import { issueLine, normaliseFailure, normaliseIssues } from './report-issues';

import type {
    BulkModule,
    ColumnDef,
    JobMode,
    RecordRow,
    RowWarning,
} from '../schemas/bulk-migration';

/**
 * What the client table (`TenantRowsTable`) shows in each layout.
 *
 * Analyze shows the module's own columns (`migrator.columns()`, from the first
 * record), the Branding preview when the module has one, and lets the operator
 * expand a client to every record it carries. Launch shows the run, its totals
 * and its issues. A module whose warnings point at one of its columns (Company
 * Information's `The value is in the "SF email" column`) opts in with
 * `launchColumns` on its report view: Launch then shows its columns too and
 * expands each record with its warnings. Branding does not opt in.
 */

export type RowsLayout = 'analysis' | 'migration';

export interface RowsTableShape {
    readonly columns: readonly ColumnDef[];
    readonly preview: boolean;
    /** The headers after the module's columns; `Records` holds the expand button. */
    readonly trailing: readonly string[];
    /** Whether each client expands to its records (the trailing `Records` cell). */
    readonly expandable: boolean;
}

export function rowsTableShape(
    module: Pick<BulkModule, 'columns' | 'supportsPreview'>,
    layout: RowsLayout,
    launchColumns: boolean,
): RowsTableShape {
    const analysis = layout === 'analysis';
    const expandable = analysis || launchColumns;
    return {
        columns: expandable ? module.columns : [],
        preview: analysis && module.supportsPreview,
        trailing: analysis
            ? ['Records']
            : ['Run', 'Totals', 'Issues', ...(launchColumns ? ['Records'] : [])],
        expandable,
    };
}

/** How many cells a row spans, for the expanded record list. */
export function rowsTableSpan(shape: RowsTableShape, selectable: boolean): number {
    return (
        (selectable ? 1 : 0) +
        2 +
        shape.columns.length +
        (shape.preview ? 1 : 0) +
        shape.trailing.length
    );
}

/** A launched record's warnings and failure, one `CODE: message` line each; none before a run. */
export function recordIssueLines(record: Pick<RecordRow, 'tracking'>): string[] {
    const warnings = normaliseIssues(record.tracking?.warnings).map(issueLine);
    const failure = normaliseFailure(record.tracking?.failure);
    return failure === null ? warnings : [...warnings, `Failed: ${issueLine(failure)}`];
}

/** The tracking status of a record that finished (migration-core `MigrationRecordStatus`). */
export const MIGRATED_STATUS = 'Migrated';

/**
 * The warnings the analysis expects a record to migrate with (INI-0035 D6: Analyze
 * is the only preview now; the operator screen no longer serves one). Shown as the
 * analysis saw them, except in a status refresh (`report`) on a record that is
 * `Migrated`: what it actually migrated with is its tracking warnings, and the
 * prediction would only repeat or contradict them. `[]` when there are none, and
 * from an API that predates them.
 */
export function plannedWarningsShown(
    record: Pick<RecordRow, 'plannedWarnings' | 'tracking'>,
    mode: JobMode,
): readonly RowWarning[] {
    if (mode === 'report' && record.tracking?.status === MIGRATED_STATUS) return [];
    return record.plannedWarnings ?? [];
}

/** One planned warning as one line: `Planned warning: CODE — message`. */
export function plannedWarningLine(warning: RowWarning): string {
    const label = warning.blocking ? 'Planned, blocking' : 'Planned warning';
    return `${label}: ${warning.code} — ${warning.message}`;
}
